import { Head, Link, router } from "@inertiajs/react"
import { Bookmark, MapPin, Trash2 } from "lucide-react"

import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent } from "@/components/ui/card"
import AppLayout from "@/layouts/app-layout"
import { POSITION_LABELS } from "@/lib/positions"
import type { SavedPlayersIndex as SavedPlayersIndexProps } from "@/types/serializers"

const AVAILABILITY_CONFIG: Record<
  string,
  { label: string; className: string }
> = {
  disponible: {
    label: "Disponible",
    className: "bg-green-500/20 text-green-400 border-green-500/30",
  },
  no_disponible: {
    label: "No disponible",
    className: "bg-neutral-500/20 text-neutral-500 border-neutral-500/30",
  },
  escuchando_ofertas: {
    label: "Escuchando ofertas",
    className: "bg-yellow-500/20 text-yellow-400 border-yellow-500/30",
  },
}

function calculateAge(dateOfBirth: string): number {
  const today = new Date()
  const birthDate = new Date(dateOfBirth)
  let age = today.getFullYear() - birthDate.getFullYear()
  const monthDiff = today.getMonth() - birthDate.getMonth()
  if (
    monthDiff < 0 ||
    (monthDiff === 0 && today.getDate() < birthDate.getDate())
  ) {
    age--
  }
  return age
}

function formatDate(dateStr: string): string {
  const date = new Date(dateStr)
  return date.toLocaleDateString("es-AR", {
    day: "numeric",
    month: "short",
    year: "numeric",
  })
}

export default function SavedPlayersIndex({
  saved_players,
}: SavedPlayersIndexProps) {
  function handleRemove(id: number) {
    router.delete(`/guardados/${id}`, { preserveState: true })
  }

  return (
    <AppLayout>
      <Head title="Jugadores guardados | KrownFT" />

      <div className="min-h-svh">
        <div className="mx-auto max-w-4xl px-4 py-6">
          <div className="mb-6 flex items-center gap-2">
            <Bookmark className="text-krown-300 h-5 w-5" />
            <h1 className="text-2xl font-bold">Jugadores guardados</h1>
          </div>

          {saved_players.length > 0 ? (
            <div className="space-y-4">
              {saved_players.map((sp) => {
                const player = sp.player_profile
                const age = calculateAge(player.date_of_birth)
                const positionLabel =
                  POSITION_LABELS[player.position] ?? player.position
                const availability =
                  AVAILABILITY_CONFIG[player.availability] ??
                  AVAILABILITY_CONFIG.disponible

                return (
                  <Card key={sp.id}>
                    <CardContent className="p-4">
                      <div className="flex items-start gap-4">
                        <Link
                          href={`/jugadores/${player.slug}`}
                          className="shrink-0"
                        >
                          <div className="bg-krown-600 h-14 w-14 rounded-full" />
                        </Link>

                        <div className="min-w-0 flex-1">
                          <Link
                            href={`/jugadores/${player.slug}`}
                            className="hover:underline"
                          >
                            <h3 className="font-semibold">
                              {player.user.full_name}
                            </h3>
                          </Link>
                          <p className="text-muted-foreground text-sm">
                            {age} años &middot; {positionLabel}
                          </p>

                          <div className="text-muted-foreground mt-1 flex flex-wrap items-center gap-2 text-sm">
                            <span className="flex items-center gap-1">
                              <MapPin className="h-3.5 w-3.5" />
                              {player.city}, {player.country}
                            </span>
                            <Badge
                              variant="outline"
                              className={availability.className}
                            >
                              {availability.label}
                            </Badge>
                          </div>

                          {sp.notes && (
                            <p className="text-muted-foreground mt-2 text-sm">
                              {sp.notes}
                            </p>
                          )}

                          <p className="text-muted-foreground mt-1 text-xs">
                            Guardado el {formatDate(sp.created_at)}
                          </p>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleRemove(sp.id)}
                          className="shrink-0 text-red-500 hover:bg-red-500/10 hover:text-red-400"
                        >
                          <Trash2 className="mr-1 h-4 w-4" />
                          Quitar
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                )
              })}
            </div>
          ) : (
            <div className="py-16 text-center">
              <Bookmark className="text-muted-foreground/50 mx-auto mb-4 h-12 w-12" />
              <p className="text-muted-foreground text-lg">
                Aún no guardaste ningún jugador
              </p>
              <p className="text-muted-foreground mt-1 text-sm">
                Usá el ícono de marcador en las tarjetas de jugadores para
                guardarlos aquí.
              </p>
              <Link href="/buscar">
                <Button variant="outline" className="mt-4">
                  Buscar jugadores
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </AppLayout>
  )
}
